/**
 * useCreateTask — the POST /tasks mutation behind the "Neue Aufgabe" affordance
 * (STEP 3). live mode fires the real create + invalidates the worklist so the new
 * row appears; mock mode resolves synchronously (no network — the queue stays
 * explorable without a backend, mirroring the rest of the Aufgaben data layer).
 *
 * tenant + actor are set server-side from the token (rule #1) — never in the body.
 *
 * [Source: apps/web/src/features/communication/hooks/useTaskActions.ts (mutation + invalidate)]
 * [Source: apps/web/src/features/aufgaben/hooks/useTaskList.ts (live/mock split)]
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TaskSummary } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import type { CreateTaskInput } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';

export function useCreateTask() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation<TaskSummary | null, Error, CreateTaskInput>({
    mutationFn: (body) => (live ? apiClient.tasks.create(body) : Promise.resolve(null)),
    onSuccess: () => {
      // Invalidate every GET /tasks list (Aufgaben queue + Eingang share the
      // ['tasks','list', …] key family) so the new row surfaces.
      void qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
  });
}
